import { FC } from 'react';
import { useRoutesAndScheduleState } from '../../contexts/routesAndSchedule.context';
import { Grid, Chip, Divider } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import PaginatedGrid from '../../../../components/paginatedGrid/PaginatedGrid';
import {
  TRowClickHandlerType,
  TViewType,
} from 'src/apis/administration/administration.interfaces';
import { useTranslation } from 'src/hooks/useTranslationHook';

interface RoutesAndScheduleGridProps {
  viewType: TViewType;
  onRowClick: (data: TRowClickHandlerType) => void;
  isLoading?: boolean;
}

const RoutesAndScheduleGrid: FC<RoutesAndScheduleGridProps> = ({
  viewType,
  onRowClick,
  isLoading = false,
}) => {
  const { translateText } = useTranslation();
  const { routes, vehicles, routeAssignments, drivers } =
    useRoutesAndScheduleState();

  const getRoutesGridColumns = (): GridColDef[] => {
    return [
      {
        field: 'routeName',
        headerName: translateText(
          'administration.routesAndSchedule.columns.routeName'
        ),
        width: 200,
        renderCell: (params) => params.row.routeName,
      },
      {
        field: 'startLocation',
        headerName: translateText(
          'administration.routesAndSchedule.columns.startLocation'
        ),
        width: 150,
        renderCell: (params) => params.row.startLocation,
      },
      {
        field: 'endLocation',
        headerName: translateText(
          'administration.routesAndSchedule.columns.endLocation'
        ),
        width: 150,
        renderCell: (params) => params.row.endLocation,
      },
      {
        field: 'stops',
        headerName: translateText(
          'administration.routesAndSchedule.columns.numberOfStops'
        ),
        width: 120,
        renderCell: (params) => params.row.stops.length,
      },
      {
        field: 'estimatedDuration',
        headerName: translateText(
          'administration.routesAndSchedule.columns.duration'
        ),
        width: 120,
        renderCell: (params) => params.row.estimatedDuration,
      },
      {
        field: 'distance',
        headerName: translateText(
          'administration.routesAndSchedule.columns.distance'
        ),
        width: 120,
        renderCell: (params) => params.row.distance,
      },
      {
        field: 'morningPickupTime',
        headerName: translateText(
          'administration.routesAndSchedule.columns.morningPickup'
        ),
        width: 130,
        renderCell: (params) => params.row.morningPickupTime,
      },
      {
        field: 'afternoonDropTime',
        headerName: translateText(
          'administration.routesAndSchedule.columns.afternoonDrop'
        ),
        width: 130,
        renderCell: (params) => params.row.afternoonDropTime,
      },
      {
        field: 'isActive',
        headerName: translateText(
          'administration.routesAndSchedule.columns.status'
        ),
        width: 100,
        renderCell: (params) => (
          <Chip
            label={
              params.row.isActive
                ? translateText(
                  'administration.routesAndSchedule.status.active'
                )
                : translateText(
                  'administration.routesAndSchedule.status.inactive'
                )
            }
            color={params.row.isActive ? 'success' : 'default'}
            size="small"
          />
        ),
      },
    ];
  };

  const getVehiclesGridColumns = (): GridColDef[] => {
    return [
      {
        field: 'vehicleNumber',
        headerName: translateText(
          'administration.routesAndSchedule.columns.vehicleNumber'
        ),
        width: 150,
        renderCell: (params) => params.row.vehicleNumber,
      },
      {
        field: 'vehicleType',
        headerName: translateText(
          'administration.routesAndSchedule.columns.type'
        ),
        width: 100,
        renderCell: (params) => params.row.vehicleType,
      },
      {
        field: 'capacity',
        headerName: translateText(
          'administration.routesAndSchedule.columns.capacity'
        ),
        width: 100,
        renderCell: (params) => params.row.capacity,
      },
      {
        field: 'driverId',
        headerName: translateText(
          'administration.routesAndSchedule.columns.driver'
        ),
        width: 150,
        renderCell: (params) => {
          const driver = drivers.find((d) => d.id === params.row.driverId);
          return driver
            ? driver.name
            : translateText(
              'administration.routesAndSchedule.status.unassigned'
            );
        },
      },
      {
        field: 'fuelType',
        headerName: translateText(
          'administration.routesAndSchedule.columns.fuelType'
        ),
        width: 100,
        renderCell: (params) => params.row.fuelType,
      },
      {
        field: 'lastMaintenanceDate',
        headerName: translateText(
          'administration.routesAndSchedule.columns.lastMaintenance'
        ),
        width: 140,
        renderCell: (params) =>
          new Date(params.row.lastMaintenanceDate).toLocaleDateString(),
      },
      {
        field: 'nextMaintenanceDate',
        headerName: translateText(
          'administration.routesAndSchedule.columns.nextMaintenance'
        ),
        width: 140,
        renderCell: (params) =>
          new Date(params.row.nextMaintenanceDate).toLocaleDateString(),
      },
      {
        field: 'isActive',
        headerName: translateText(
          'administration.routesAndSchedule.columns.status'
        ),
        width: 100,
        renderCell: (params) => (
          <Chip
            label={
              params.row.isActive
                ? translateText(
                  'administration.routesAndSchedule.status.active'
                )
                : translateText(
                  'administration.routesAndSchedule.status.inactive'
                )
            }
            color={params.row.isActive ? 'success' : 'default'}
            size="small"
          />
        ),
      },
    ];
  };

  const getAssignmentsGridColumns = (): GridColDef[] => {
    return [
      {
        field: 'routeId',
        headerName: translateText(
          'administration.routesAndSchedule.columns.route'
        ),
        width: 150,
        renderCell: (params) => {
          const route = routes.find((r) => r.id === params.row.routeId);
          return route
            ? route.routeName
            : translateText(
              'administration.routesAndSchedule.status.unknownRoute'
            );
        },
      },
      {
        field: 'vehicleId',
        headerName: translateText(
          'administration.routesAndSchedule.columns.vehicle'
        ),
        width: 150,
        renderCell: (params) => {
          const vehicle = vehicles.find((v) => v.id === params.row.vehicleId);
          return vehicle
            ? vehicle.vehicleNumber
            : translateText(
              'administration.routesAndSchedule.status.unknownVehicle'
            );
        },
      },
      {
        field: 'driverId',
        headerName: translateText(
          'administration.routesAndSchedule.columns.driver'
        ),
        width: 150,
        renderCell: (params) => {
          const driver = drivers.find((d) => d.id === params.row.driverId);
          return driver
            ? driver.name
            : translateText(
              'administration.routesAndSchedule.status.unknownDriver'
            );
        },
      },
      {
        field: 'assignedStudents',
        headerName: translateText(
          'administration.routesAndSchedule.columns.studentsAssigned'
        ),
        width: 120,
        renderCell: (params) => params.row.assignedStudents.length,
      },
      {
        field: 'academicYear',
        headerName: translateText(
          'administration.routesAndSchedule.columns.academicYear'
        ),
        width: 130,
        renderCell: (params) => params.row.academicYear,
      },
      {
        field: 'startDate',
        headerName: translateText(
          'administration.routesAndSchedule.columns.startDate'
        ),
        width: 120,
        renderCell: (params) =>
          new Date(params.row.startDate).toLocaleDateString(),
      },
      {
        field: 'endDate',
        headerName: translateText(
          'administration.routesAndSchedule.columns.endDate'
        ),
        width: 120,
        renderCell: (params) =>
          params.row.endDate
            ? new Date(params.row.endDate).toLocaleDateString()
            : translateText('administration.routesAndSchedule.status.ongoing'),
      },
      {
        field: 'isActive',
        headerName: translateText(
          'administration.routesAndSchedule.columns.status'
        ),
        width: 100,
        renderCell: (params) => (
          <Chip
            label={
              params.row.isActive
                ? translateText(
                  'administration.routesAndSchedule.status.active'
                )
                : translateText(
                  'administration.routesAndSchedule.status.inactive'
                )
            }
            color={params.row.isActive ? 'success' : 'default'}
            size="small"
          />
        ),
      },
    ];
  };

  const getSalariesGridColumns = (): GridColDef[] => {
    return [
      {
        field: 'name',
        headerName: translateText(
          'administration.routesAndSchedule.columns.driverName'
        ),
        width: 180,
        renderCell: (params) => params.row.name,
      },
      {
        field: 'licenseNumber',
        headerName: translateText(
          'administration.routesAndSchedule.columns.licenseNumber'
        ),
        width: 180,
        renderCell: (params) => params.row.licenseNumber,
      },
      {
        field: 'phoneNumber',
        headerName: translateText(
          'administration.routesAndSchedule.columns.phoneNumber'
        ),
        width: 140,
        renderCell: (params) => params.row.phoneNumber,
      },
      {
        field: 'salary',
        headerName: translateText(
          'administration.routesAndSchedule.columns.salary'
        ),
        width: 120,
        renderCell: (params) => params.row.salary.toLocaleString(),
      },
      {
        field: 'salaryStatus',
        headerName: translateText(
          'administration.routesAndSchedule.columns.salaryStatus'
        ),
        width: 120,
        renderCell: (params) => (
          <Chip
            label={params.row.salaryStatus}
            color={
              params.row.salaryStatus === 'PAID'
                ? 'success'
                : params.row.salaryStatus === 'PENDING'
                  ? 'warning'
                  : 'error'
            }
            size="small"
          />
        ),
      },
      {
        field: 'lastSalaryPaidDate',
        headerName: translateText(
          'administration.routesAndSchedule.columns.lastPaidDate'
        ),
        width: 140,
        renderCell: (params) =>
          params.row.lastSalaryPaidDate
            ? new Date(params.row.lastSalaryPaidDate).toLocaleDateString()
            : translateText('administration.routesAndSchedule.status.notPaid'),
      },
      {
        field: 'nextSalaryDueDate',
        headerName: translateText(
          'administration.routesAndSchedule.columns.nextDueDate'
        ),
        width: 140,
        renderCell: (params) =>
          new Date(params.row.nextSalaryDueDate).toLocaleDateString(),
      },
      {
        field: 'experience',
        headerName: translateText(
          'administration.routesAndSchedule.columns.experience'
        ),
        width: 140,
        renderCell: (params) => params.row.experience,
      },
      {
        field: 'isActive',
        headerName: translateText(
          'administration.routesAndSchedule.columns.status'
        ),
        width: 100,
        renderCell: (params) => (
          <Chip
            label={
              params.row.isActive
                ? translateText(
                  'administration.routesAndSchedule.status.active'
                )
                : translateText(
                  'administration.routesAndSchedule.status.inactive'
                )
            }
            color={params.row.isActive ? 'success' : 'default'}
            size="small"
          />
        ),
      },
    ];
  };

  const getGridColumns = () => {
    switch (viewType) {
      case 'routes':
        return getRoutesGridColumns();
      case 'vehicles':
        return getVehiclesGridColumns();
      case 'assignments':
        return getAssignmentsGridColumns();
      case 'salaries':
        return getSalariesGridColumns();
      default:
        return getRoutesGridColumns();
    }
  };

  const getGridRows = () => {
    switch (viewType) {
      case 'routes':
        return routes;
      case 'vehicles':
        return vehicles;
      case 'assignments':
        return routeAssignments;
      case 'salaries':
        return drivers;
      default:
        return routes;
    }
  };

  const handleRowClick = (params: any) => {
    const data = params.row;
    onRowClick(data);
  };

  const columns = getGridColumns();
  const rows = getGridRows();

  return (
    <Grid
      container
      spacing={{ xs: 1, sm: 2 }}
      data-testid={`routes-schedule-grid-${viewType}`}
    >
      <Grid item xs={12}>
        <PaginatedGrid
          dataTestId={`routes-schedule-${viewType}-grid`}
          rows={rows}
          columns={columns}
          onRowClick={handleRowClick}
          getRowClassName={(params) => 'cursor-pointer'}
          hideColumnsOnMobile={['id', 'actions']}
        />
      </Grid>
      {viewType !== TViewType.SALARIES && (
        <Grid item xs={12} sx={{ mt: { xs: 1, sm: 2 } }}>
          <Divider />
        </Grid>
      )}
    </Grid>
  );
};

export default RoutesAndScheduleGrid;
